import React, { useState } from 'react';
import styled from 'styled-components';
import { ArrowDownTrayIcon, CodeBracketIcon } from '@heroicons/react/24/outline';
import { GlobeAltIcon } from '@heroicons/react/24/solid';

const DownloadSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.background};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.text.primary};
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  text-align: center;
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const DownloadGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const DownloadCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  }
`;

const IconWrapper = styled.div`
  width: 3rem;
  height: 3rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 auto 1rem;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.text.primary};
`;

const CardDescription = styled.p`
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  transition: background 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.primaryDark};
  }

  svg {
    width: 1.2rem;
    height: 1.2rem;
  }
`;

const Instructions = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  padding: 2rem;
  margin-top: 2rem;
`;

const InstructionTitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.text.primary};
`;

const InstructionList = styled.ol`
  list-style: decimal;
  padding-left: 1.5rem;
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.8;

  li {
    margin-bottom: 0.5rem;
  }

  code {
    background: ${props => props.theme.colors.background};
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace;
    font-size: 0.9em;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background: ${props => props.theme.colors.primaryLight};
  color: ${props => props.theme.colors.primary};
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  margin-left: 0.5rem;
`;

interface ExtensionDownloadProps {
  environment?: 'dev' | 'stg' | 'prd';
}

const ExtensionDownload: React.FC<ExtensionDownloadProps> = ({ environment = 'dev' }) => {
  const [showInstructions, setShowInstructions] = useState(false);

  const getDownloadUrl = () => {
    switch (environment) {
      case 'dev':
        return '/downloads/promptasaurus-extension-dev.zip';
      case 'stg':
        return '/downloads/promptasaurus-extension-stg.zip';
      case 'prd':
        return '/downloads/promptasaurus-extension-prd.zip';
      default:
        return '/downloads/promptasaurus-extension-dev.zip';
    }
  };

  const getEnvironmentLabel = () => {
    switch (environment) {
      case 'dev':
        return 'Development';
      case 'stg':
        return 'Staging';
      case 'prd':
        return 'Production';
      default:
        return 'Development';
    }
  };

  return (
    <DownloadSection>
      <Container>
        <Title>Test the Extension</Title>
        <Subtitle>
          Get early access to Promptasaurus and help shape the future of AI prompting
        </Subtitle>

        <DownloadGrid>
          <DownloadCard>
            <IconWrapper>
              <GlobeAltIcon />
            </IconWrapper>
            <CardTitle>
              Chrome Extension
              <Badge>{getEnvironmentLabel()}</Badge>
            </CardTitle>
            <CardDescription>
              Download the pre-built extension for Chrome, Edge, and other Chromium browsers
            </CardDescription>
            <DownloadButton 
              href={getDownloadUrl()}
              download
              onClick={() => setShowInstructions(true)}
            >
              <ArrowDownTrayIcon />
              Download Extension
            </DownloadButton>
          </DownloadCard>

          <DownloadCard>
            <IconWrapper>
              <CodeBracketIcon />
            </IconWrapper>
            <CardTitle>Build from Source</CardTitle>
            <CardDescription>
              Clone the repository and build the extension yourself with custom configurations
            </CardDescription>
            <DownloadButton 
              href="https://github.com/gruntwork/promptasaurus"
              target="_blank"
              rel="noopener noreferrer"
            >
              <CodeBracketIcon />
              View on GitHub
            </DownloadButton>
          </DownloadCard>
        </DownloadGrid>

        {showInstructions && (
          <Instructions>
            <InstructionTitle>Installation Instructions</InstructionTitle>
            <InstructionList>
              <li>Download the extension ZIP file above</li>
              <li>Unzip the file to a folder on your computer</li>
              <li>Open Chrome and navigate to <code>chrome://extensions/</code></li>
              <li>Enable "Developer mode" using the toggle in the top right</li>
              <li>Click "Load unpacked" and select the unzipped folder</li>
              <li>The Promptasaurus icon should appear in your extensions bar</li>
              <li>Visit ChatGPT or Claude to start improving your prompts!</li>
            </InstructionList>
          </Instructions>
        )}
      </Container>
    </DownloadSection>
  );
};

export default ExtensionDownload;